import { useState } from "react";
import styled, { css } from "styled-components";
import Loader from "./Loader";

const Styled = {
  Page: styled.div`
    background: linear-gradient(119.36deg, #071f30 28.2%, #0f4348 100%);
    padding: 24px 56px;
    flex: 1;

    h1 {
      font-style: normal;
      font-family: "Poppins", sans-serif;
      font-weight: 900;
      font-size: 64px;
      line-height: 96px;
      margin: 0;
      color: rgba(255, 255, 255, 0.9);
    }

    .h1-green {
      color: #1eb18e;
    }

    header {
      display: flex;
      justify-content: space-between;
      width: 100%;
      margin-bottom: 48px;

      a {
        color: #219af1;
        text-decoration: none;

        svg {
          margin-left: 8px;
        }

        &:hover {
          text-decoration: underline;
        }
      }
    }

    ul {
      margin: 48px 0;
      padding: 0;
      list-style: none;
      color: rgba(255, 255, 255, 0.6);
    }

    li {
      display: flex;
      align-items: center;

      &:not(:last-child) {
        margin-bottom: 16px;
      }

      svg {
        margin-right: 4px;
      }
    }

    form {
      & > div {
        display: flex;
        align-items: center;
      }
    }

    input[type="text"] {
      display: flex;
      align-items: center;
      font-size: 15px;
      line-height: 20px;
      padding: 6px 8px;
      width: 320px;
      height: 48px;
      background: rgba(255, 255, 255, 0.1);
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: 16px;
      box-sizing: border-box;
      color: rgba(255, 255, 255, 0.9);
      margin-right: 8px;

      &::placeholder {
        color: rgba(255, 255, 255, 0.6);
      }

      &:focus {
        outline: none;
        border: 2px solid rgba(255, 255, 255, 0.4) !important;
      }

      &:hover {
        border: 1px solid rgba(255, 255, 255, 0.4);
      }

      &.error {
        outline: none;
        border: 1px solid rgba(237, 95, 106, 0.6);
      }
    }

    @media screen and (max-width: 768px) {
      padding: 24px;

      h1 {
        font-size: 34px;
        line-height: 46px;
      }

      form {
        & > div {
          flex-direction: column;
          flex-grow: 1;
          width: 100%;

          input,
          button {
            width: 100% !important;
            margin: 0 !important;
          }

          input {
            margin-bottom: 16px !important;
          }
        }
      }
    }
  `,
  Button: styled.button`
    width: 192px;
    height: 48px;
    background: #219af1;
    border: 1px solid #077cd0;
    border-radius: 16px;
    color: #ffffff;
    cursor: pointer;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 6px 16px;
    position: relative;

    ${({ disabled }) => {
      return (
        disabled &&
        css`
          background: rgba(255, 255, 255, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.1);
          color: #1eb18e;

          &:focus,
          &:hover {
            background: rgba(255, 255, 255, 0.1) !important;
            border: 1px solid rgba(255, 255, 255, 0.1) !important;
            outline: none !important;
          }
        `
      );
    }}

    &:focus,
    &:hover {
      outline: none;
      background: #077cd0;
    }

    span {
      margin-right: 8px;
    }
  `,
  ErrorHolder: styled.div`
    margin-top: 16px;
    color: #9b4e5b;
  `,
};

function App() {
  const [isJoinedWaitlist, setIsJoinedWaitlist] = useState<boolean>(false);
  const [isSending, setIsSending] = useState<boolean>(false);

  const [input, setInput] = useState<string>("");

  const [error, setError] = useState<string>("");

  const handleJoinWaitlistClick = async () => {
    const { REACT_APP_BACKEND_URL } = process.env;

    const URL = `${REACT_APP_BACKEND_URL}/api/waitlist/users`;

    setIsSending(true);

    try {
      const emailToObject = { email: input };

      const response = await fetch(URL, { method: "POST", body: JSON.stringify(emailToObject) });

      const json = await response.json();

      if (json.request.error) {
        setError("Server error. Try again later.");

        if (response.status === 400) {
          const { description } = json.request.error;

          setError(description);

          if (description === `Parameter "email" must be valid email address`) {
            setError("Email is not valid");
          }

          throw new Error(error);
        }
      }

      setIsJoinedWaitlist(true);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Styled.Page>
      <header>
        <svg width="118" height="24" viewBox="0 0 118 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M8.07375 23.9703C6.56115 23.9703 5.2057 23.7231 4.00741 23.2287C2.80912 22.7342 1.84655 22.0025 1.11972 21.0334C0.412528 20.0643 0.0392883 18.8974 0 17.5328H5.36286C5.44143 18.3041 5.70663 18.8974 6.15845 19.3127C6.61026 19.7083 7.19959 19.9061 7.92642 19.9061C8.6729 19.9061 9.26223 19.7379 9.6944 19.4017C10.1266 19.0457 10.3427 18.5612 10.3427 17.9481C10.3427 17.4339 10.1659 17.0087 9.81226 16.6724C9.47831 16.3362 9.05596 16.0593 8.54521 15.8418C8.05411 15.6242 7.34692 15.377 6.42364 15.1001C5.08784 14.6848 3.99759 14.2695 3.15289 13.8541C2.30819 13.4388 1.58136 12.8257 0.972386 12.0148C0.363417 11.204 0.0589321 10.1459 0.0589321 8.84054C0.0589321 6.90235 0.756301 5.38937 2.15104 4.30161C3.54577 3.19407 5.36286 2.6403 7.60229 2.6403C9.88102 2.6403 11.7177 3.19407 13.1125 4.30161C14.5072 5.38937 15.2537 6.91224 15.3519 8.87021H9.90066C9.86137 8.19778 9.61582 7.67367 9.164 7.2979C8.71219 6.90235 8.13269 6.70457 7.4255 6.70457C6.81653 6.70457 6.32542 6.87268 5.95218 7.2089C5.57894 7.52534 5.39232 7.99011 5.39232 8.60321C5.39232 9.27565 5.70663 9.79975 6.33525 10.1755C6.96386 10.5513 7.94607 10.9567 9.28187 11.3918C10.6177 11.8467 11.6981 12.2818 12.5232 12.6972C13.3679 13.1125 14.0947 13.7157 14.7037 14.5068C15.3126 15.2979 15.6171 16.3164 15.6171 17.5624C15.6171 18.7491 15.3126 19.827 14.7037 20.796C14.1143 21.7651 13.25 22.5365 12.1106 23.11C10.9713 23.6836 9.62564 23.9703 8.07375 23.9703ZM28.825 7.0309C30.7108 7.0309 32.2234 7.66378 33.3628 8.92954C34.5021 10.1755 35.0718 11.8962 35.0718 14.0915V23.7627H30.0625V14.7738C30.0625 13.6663 29.7777 12.8059 29.208 12.1928C28.6383 11.5797 27.8722 11.2732 26.9096 11.2732C25.9471 11.2732 25.181 11.5797 24.6113 12.1928C24.0416 12.8059 23.7568 13.6663 23.7568 14.7738V23.7627H18.718V1.80964H23.7568V9.43387C24.2675 8.7021 24.9649 8.11867 25.8489 7.68356C26.7328 7.24846 27.7249 7.0309 28.825 7.0309ZM41.1073 5.48826C40.2233 5.48826 39.4964 5.23115 38.9268 4.71694C38.3767 4.18294 38.1017 3.53028 38.1017 2.75896C38.1017 1.96786 38.3767 1.3152 38.9268 0.800989C39.4964 0.266996 40.2233 0 41.1073 0C41.9716 0 42.6788 0.266996 43.2288 0.800989C43.7985 1.3152 44.0834 1.96786 44.0834 2.75896C44.0834 3.53028 43.7985 4.18294 43.2288 4.71694C42.6788 5.23115 41.9716 5.48826 41.1073 5.48826ZM43.6119 7.2089V23.7627H38.5732V7.2089H43.6119ZM51.9781 19.5797H59.1089V23.7627H46.35V19.7281L53.1862 11.3918H46.4088V7.2089H58.9616V11.2435L51.9781 19.5797ZM78.1174 7.2089V23.7627H73.0787V21.508C72.5679 22.2398 71.8705 22.8331 70.9866 23.288C70.1222 23.7231 69.1596 23.9407 68.0989 23.9407C66.8416 23.9407 65.7317 23.6638 64.7692 23.11C63.8066 22.5365 63.0601 21.7157 62.5297 20.6477C61.9993 19.5797 61.7342 18.3239 61.7342 16.8801V7.2089H66.7434V16.1978C66.7434 17.3053 67.0283 18.1656 67.5979 18.7787C68.1676 19.3918 68.9337 19.6984 69.8963 19.6984C70.8785 19.6984 71.6545 19.3918 72.2241 18.7787C72.7938 18.1656 73.0787 17.3053 73.0787 16.1978V7.2089H78.1174ZM91.7551 7.0309C93.6803 7.0309 95.2122 7.66378 96.352 8.92954C97.5112 10.1755 98.0903 11.8962 98.0903 14.0915V23.7627H93.0811V14.7738C93.0811 13.6663 92.7963 12.8059 92.2266 12.1928C91.6569 11.5797 90.8908 11.2732 89.9282 11.2732C88.9657 11.2732 88.1995 11.5797 87.6299 12.1928C87.0602 12.8059 86.7754 13.6663 86.7754 14.7738V23.7627H81.7366V7.2089H86.7754V9.4042C87.2861 8.67244 87.9736 8.09889 88.838 7.68356C89.7023 7.24846 90.6747 7.0309 91.7551 7.0309ZM117.091 15.2188C117.091 15.6934 117.061 16.1879 117.003 16.7021H105.599C105.678 17.7305 106.002 18.5216 106.572 19.0754C107.161 19.6094 107.878 19.8764 108.723 19.8764C109.98 19.8764 110.854 19.3424 111.345 18.2744H116.708C116.433 19.3622 115.932 20.3412 115.205 21.2114C114.498 22.0816 113.604 22.7639 112.524 23.2583C111.443 23.7528 110.235 24 108.9 24C107.288 24 105.855 23.6539 104.597 22.9617C103.34 22.2695 102.358 21.2806 101.651 19.9951C100.944 18.7095 100.59 17.2064 100.59 15.4858C100.59 13.7651 100.934 12.2621 101.621 10.9765C102.328 9.69098 103.31 8.7021 104.568 8.00989C105.825 7.31768 107.269 6.97157 108.9 6.97157C110.491 6.97157 111.905 7.30779 113.143 7.98022C114.381 8.65266 115.342 9.61187 116.03 10.8579C116.738 12.1038 117.091 13.5575 117.091 15.2188ZM111.934 13.8838C111.934 13.0136 111.64 12.3214 111.05 11.8072C110.461 11.293 109.724 11.0358 108.84 11.0358C107.996 11.0358 107.279 11.2831 106.69 11.7775C106.12 12.2719 105.766 12.974 105.629 13.8838H111.934Z"
            fill="#2ED1AA"
          />
        </svg>
        <a href="https://shizune.co/?utm_source=investor-updates" target="_blank" rel="noreferrer">
          <span>Find investors</span>
          <svg width="10" height="11" viewBox="0 0 10 11" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M9.93529 5.67893C9.89562 5.57664 9.83615 5.48318 9.76029 5.40393L5.59362 1.23726C5.51592 1.15957 5.42368 1.09793 5.32216 1.05588C5.22064 1.01383 5.11184 0.992187 5.00195 0.992188C4.78004 0.992187 4.56721 1.08034 4.41029 1.23726C4.33259 1.31496 4.27095 1.4072 4.2289 1.50872C4.18685 1.61024 4.16521 1.71905 4.16521 1.82893C4.16521 2.05085 4.25337 2.26368 4.41029 2.4206L7.16029 5.16226H0.835286C0.614273 5.16226 0.402311 5.25006 0.246031 5.40634C0.0897507 5.56262 0.00195312 5.77458 0.00195312 5.9956C0.00195312 6.21661 0.0897507 6.42857 0.246031 6.58485C0.402311 6.74113 0.614273 6.82893 0.835286 6.82893H7.16029L4.41029 9.5706C4.33218 9.64807 4.27018 9.74023 4.22788 9.84178C4.18557 9.94333 4.16379 10.0523 4.16379 10.1623C4.16379 10.2723 4.18557 10.3812 4.22788 10.4827C4.27018 10.5843 4.33218 10.6765 4.41029 10.7539C4.48776 10.832 4.57992 10.894 4.68147 10.9363C4.78302 10.9786 4.89194 11.0004 5.00195 11.0004C5.11196 11.0004 5.22088 10.9786 5.32243 10.9363C5.42398 10.894 5.51615 10.832 5.59362 10.7539L9.76029 6.58726C9.83615 6.50801 9.89562 6.41456 9.93529 6.31226C10.0186 6.10938 10.0186 5.88182 9.93529 5.67893Z"
              fill="#219AF1"
            />
          </svg>
        </a>
      </header>
      <h1>
        Get the most of your investors <br />
        <span className="h1-green">with investor updates</span>
      </h1>
      <ul>
        <li>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M18.7104 7.20986C18.6175 7.11613 18.5069 7.04174 18.385 6.99097C18.2632 6.9402 18.1324 6.91406 18.0004 6.91406C17.8684 6.91406 17.7377 6.9402 17.6159 6.99097C17.494 7.04174 17.3834 7.11613 17.2904 7.20986L9.84044 14.6699L6.71044 11.5299C6.61392 11.4366 6.49998 11.3633 6.37512 11.3141C6.25026 11.2649 6.11694 11.2408 5.98276 11.2431C5.84858 11.2454 5.71617 11.2741 5.59309 11.3276C5.47001 11.3811 5.35868 11.4583 5.26544 11.5549C5.1722 11.6514 5.09889 11.7653 5.04968 11.8902C5.00048 12.015 4.97635 12.1484 4.97867 12.2825C4.98099 12.4167 5.00972 12.5491 5.06321 12.6722C5.1167 12.7953 5.19392 12.9066 5.29044 12.9999L9.13044 16.8399C9.2234 16.9336 9.334 17.008 9.45586 17.0588C9.57772 17.1095 9.70843 17.1357 9.84044 17.1357C9.97245 17.1357 10.1032 17.1095 10.225 17.0588C10.3469 17.008 10.4575 16.9336 10.5504 16.8399L18.7104 8.67986C18.8119 8.58622 18.893 8.47257 18.9484 8.34607C19.0038 8.21957 19.0324 8.08296 19.0324 7.94486C19.0324 7.80676 19.0038 7.67015 18.9484 7.54365C18.893 7.41715 18.8119 7.3035 18.7104 7.20986V7.20986Z"
              fill="#1EB18E"
            />
          </svg>
          <span>More clients</span>
        </li>
        <li>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M18.7104 7.20986C18.6175 7.11613 18.5069 7.04174 18.385 6.99097C18.2632 6.9402 18.1324 6.91406 18.0004 6.91406C17.8684 6.91406 17.7377 6.9402 17.6159 6.99097C17.494 7.04174 17.3834 7.11613 17.2904 7.20986L9.84044 14.6699L6.71044 11.5299C6.61392 11.4366 6.49998 11.3633 6.37512 11.3141C6.25026 11.2649 6.11694 11.2408 5.98276 11.2431C5.84858 11.2454 5.71617 11.2741 5.59309 11.3276C5.47001 11.3811 5.35868 11.4583 5.26544 11.5549C5.1722 11.6514 5.09889 11.7653 5.04968 11.8902C5.00048 12.015 4.97635 12.1484 4.97867 12.2825C4.98099 12.4167 5.00972 12.5491 5.06321 12.6722C5.1167 12.7953 5.19392 12.9066 5.29044 12.9999L9.13044 16.8399C9.2234 16.9336 9.334 17.008 9.45586 17.0588C9.57772 17.1095 9.70843 17.1357 9.84044 17.1357C9.97245 17.1357 10.1032 17.1095 10.225 17.0588C10.3469 17.008 10.4575 16.9336 10.5504 16.8399L18.7104 8.67986C18.8119 8.58622 18.893 8.47257 18.9484 8.34607C19.0038 8.21957 19.0324 8.08296 19.0324 7.94486C19.0324 7.80676 19.0038 7.67015 18.9484 7.54365C18.893 7.41715 18.8119 7.3035 18.7104 7.20986V7.20986Z"
              fill="#1EB18E"
            />
          </svg>
          <span>New partnerships</span>
        </li>
        <li>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M18.7104 7.20986C18.6175 7.11613 18.5069 7.04174 18.385 6.99097C18.2632 6.9402 18.1324 6.91406 18.0004 6.91406C17.8684 6.91406 17.7377 6.9402 17.6159 6.99097C17.494 7.04174 17.3834 7.11613 17.2904 7.20986L9.84044 14.6699L6.71044 11.5299C6.61392 11.4366 6.49998 11.3633 6.37512 11.3141C6.25026 11.2649 6.11694 11.2408 5.98276 11.2431C5.84858 11.2454 5.71617 11.2741 5.59309 11.3276C5.47001 11.3811 5.35868 11.4583 5.26544 11.5549C5.1722 11.6514 5.09889 11.7653 5.04968 11.8902C5.00048 12.015 4.97635 12.1484 4.97867 12.2825C4.98099 12.4167 5.00972 12.5491 5.06321 12.6722C5.1167 12.7953 5.19392 12.9066 5.29044 12.9999L9.13044 16.8399C9.2234 16.9336 9.334 17.008 9.45586 17.0588C9.57772 17.1095 9.70843 17.1357 9.84044 17.1357C9.97245 17.1357 10.1032 17.1095 10.225 17.0588C10.3469 17.008 10.4575 16.9336 10.5504 16.8399L18.7104 8.67986C18.8119 8.58622 18.893 8.47257 18.9484 8.34607C19.0038 8.21957 19.0324 8.08296 19.0324 7.94486C19.0324 7.80676 19.0038 7.67015 18.9484 7.54365C18.893 7.41715 18.8119 7.3035 18.7104 7.20986V7.20986Z"
              fill="#1EB18E"
            />
          </svg>
          <span>Fundraise faster</span>
        </li>
      </ul>
      <form onSubmit={(event) => event.preventDefault()}>
        <div>
          <input
            type="text"
            className={error && "error"}
            placeholder="Your email..."
            value={input}
            onInput={(event) => {
              setError("");
              setInput((event.target as HTMLInputElement).value);
            }}
          />
          <Styled.Button onClick={handleJoinWaitlistClick} disabled={isJoinedWaitlist || isSending}>
            {isSending ? (
              <Loader width="24px" height="24px" noPadding centered />
            ) : (
              <>
                {isJoinedWaitlist ? (
                  "Email sent."
                ) : (
                  <>
                    <span>Join waitlist</span>
                    <svg width="10" height="11" viewBox="0 0 10 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M9.93529 5.67893C9.89562 5.57664 9.83615 5.48318 9.76029 5.40393L5.59362 1.23726C5.51592 1.15957 5.42368 1.09793 5.32216 1.05588C5.22064 1.01383 5.11184 0.992187 5.00195 0.992188C4.78004 0.992187 4.56721 1.08034 4.41029 1.23726C4.33259 1.31496 4.27095 1.4072 4.2289 1.50872C4.18685 1.61024 4.16521 1.71905 4.16521 1.82893C4.16521 2.05085 4.25337 2.26368 4.41029 2.4206L7.16029 5.16226H0.835286C0.614273 5.16226 0.402311 5.25006 0.246031 5.40634C0.0897507 5.56262 0.00195312 5.77458 0.00195312 5.9956C0.00195312 6.21661 0.0897507 6.42857 0.246031 6.58485C0.402311 6.74113 0.614273 6.82893 0.835286 6.82893H7.16029L4.41029 9.5706C4.33218 9.64807 4.27018 9.74023 4.22788 9.84178C4.18557 9.94333 4.16379 10.0523 4.16379 10.1623C4.16379 10.2723 4.18557 10.3812 4.22788 10.4827C4.27018 10.5843 4.33218 10.6765 4.41029 10.7539C4.48776 10.832 4.57992 10.894 4.68147 10.9363C4.78302 10.9786 4.89194 11.0004 5.00195 11.0004C5.11196 11.0004 5.22088 10.9786 5.32243 10.9363C5.42398 10.894 5.51615 10.832 5.59362 10.7539L9.76029 6.58726C9.83615 6.50801 9.89562 6.41456 9.93529 6.31226C10.0186 6.10938 10.0186 5.88182 9.93529 5.67893Z"
                        fill="white"
                      />
                    </svg>
                  </>
                )}
              </>
            )}
          </Styled.Button>
        </div>
        {error && <Styled.ErrorHolder>{error}</Styled.ErrorHolder>}
      </form>
    </Styled.Page>
  );
}

export default App;
